import {Component, EventEmitter, Input, OnInit, Output, ViewChild} from '@angular/core';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {Store} from '@ngxs/store';
import {IMeasurement, IMeasurementUnit, MeasurementAction, MeasurementState} from '../../models/measurement';
import {firstValueFrom, Observable} from 'rxjs';
import {NgForm} from '@angular/forms';
import {Loader} from '../../models/loader/loader.redux';
import {PatientMeasurementRequestDTO} from '../../dtos/measurement';
import {DateHelper} from '../../class/helper/date.helper.class';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {Logger} from '../../class/helper/logger.class';
import {cloneDeep} from 'lodash';

export enum MeasurementRowEnum {
  NEW = 'NEW',
  FILL = 'FILL'
}

export interface IMeasurementRow {
  _id?: string;
  status?: MeasurementRowEnum;
  name: string;
  value: number;
  unitMeasurement: IMeasurementUnit[];
  unitMeasurementDefault: IMeasurementUnit;
  unit: string;
  lastValue?: number;
  measurementSelected?: IMeasurement;
  showEdit?: boolean;
  saved?: boolean;
  fromTemplate?: boolean;
  name_es?: string;
}

@Component({
  selector: 'app-new-measurement',
  templateUrl: './new-measurement.component.html',
  styleUrls: ['./new-measurement.component.scss']
})
export class NewMeasurementComponent implements OnInit {
  // tslint:disable:no-redundant-jsdoc
  measurementsType$: Observable<IMeasurement[]> = this.store.select<IMeasurement[]>(MeasurementState.getMeasurementsType);
  isLoadingMenus: boolean;
  isLoadingFirstMenus: boolean;
  finishSearchMenus: boolean;
  searchTempValue: string;
  measurementRowEnum = MeasurementRowEnum;
  totalBatches: number;
  searchInterval: any;
  perPage = 20;
  dateHelper: DateHelper;
  @ViewChild('formMeasurementTable') formMeasurementTable: NgForm;
  @Input() measurementsRow: IMeasurementRow[] = [];
  @Input() appointmentId: string;
  @Input() patientId: string;
  @Input() expertId: string;
  @Input() serviceId: string;
  @Input() clinicId: string;
  @Input() showBtnAction = true;
  @Input() showLastValue = false;
  @Input() required = true;
  @Input() forTemplates: boolean;
  @Input() addLastInput: boolean;
  @Input() showIsTemplate: boolean;
  @Input() tableIndex: number;
  @Output() changeRow: EventEmitter<number> = new EventEmitter<number>();
  @Output() save: EventEmitter<PatientMeasurementRequestDTO> = new EventEmitter<PatientMeasurementRequestDTO>();
  @Output() validRow: EventEmitter<IMeasurementRow> = new EventEmitter<IMeasurementRow>();
  @Output() removeRow: EventEmitter<string> = new EventEmitter<string>();

  formatterDecimals = (value: number) => `${!isNaN(value) ? +value?.toFixed(2) : ''}`;

  constructor(
    public utilitiesService: UtilitiesService,
    private store: Store,
    private messageAlertService: MessageAlertService
  ) { }

  ngOnInit(): void {
    this.initValues();
    this.dateHelper = new DateHelper();
  }

  /**
   * Inicializa las propiedades necesarias
   */
  initValues(): void {
    this.setValuesSearchMeasurement();
    try {
      if (this.addLastInput) {
        this.addNewMeasurementRow();
      }
    } catch (e) {
      Logger.log('Ocurrio un error', e?.message);
    }
  }

  /**
   * Inicializa las propiedades referentes a la busuqeda de mediciones.
   */
  setValuesSearchMeasurement(): void {
    this.isLoadingMenus = false;
    this.isLoadingFirstMenus = false;
    this.finishSearchMenus = false;
    this.totalBatches = 0;
    this.searchTempValue = '';
  }

  /**
   * Emite al componente padre cuando una fila es válida.
   * @param index
   */
  onEmitVerifyValidRow(index: number): void {
    if (this.measurementsRow[index]._id &&
      this.measurementsRow[index].status === this.measurementRowEnum.FILL &&
      this.measurementsRow[index].unit &&
      this.measurementsRow[index].value) {
      this.validRow.emit(cloneDeep(this.measurementsRow[index]));
    }
  }

  /**
   * Se selecciona una medida.
   * @param value
   * @param index
   * @returns
   */
  async onSelectMeasurement(value, index): Promise<void> {
    await firstValueFrom((this.store.dispatch(new Loader(true))));
    try {
      const measurements = cloneDeep(await firstValueFrom((this.store.selectOnce(MeasurementState.getMeasurementsType))));
      const selected = measurements.find(item => item._id === value);
      if (!this.measurementsRow[index] || !selected) {
        await firstValueFrom((this.store.dispatch(new Loader(false))));
        return;
      }
      if (this.measurementsRow[index].status === this.measurementRowEnum.NEW) {
        this.addNewMeasurementRow();
      }
      this.measurementsRow[index] = {
        _id: selected._id,
        status: this.measurementRowEnum.FILL,
        name: selected.name,
        name_es: selected.name_es,
        unitMeasurement: selected.measureUnits,
        unitMeasurementDefault: null,
        unit: '',
        value: null
      };
      // verificar si en el historial ya existe un registro de esta medida para ya no permitirle cambiar el tipo de medida
      const historialData = cloneDeep(await firstValueFrom((this.store.selectOnce(MeasurementState.getMeasurementsHistorialHeaders))));
      if (historialData) {
        const measure = historialData.find(item => item._id === this.measurementsRow[index]._id);
        if (measure) {
          this.measurementsRow[index].unitMeasurementDefault = measure.defaultMeasurement;
          this.measurementsRow[index].unit = measure.defaultMeasurement._id;
          this.measurementsRow[index].lastValue = measure.lastValue;
        }
      }
      this.onEmitVerifyValidRow(index);
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
    }
    await firstValueFrom((this.store.dispatch(new Loader(false))));
  }

  /**
   * Agrega una nueva fila con valores default
   */
  addNewMeasurementRow(): void {
    if (!this.measurementsRow) {
      this.measurementsRow = [];
    }
    this.measurementsRow = [
      ...this.measurementsRow,
      {
        _id: '',
        status: this.measurementRowEnum.NEW,
        value: null,
        name: '',
        unitMeasurement: [],
        unitMeasurementDefault: null,
        unit: ''
      }
    ];
  }

  /**
   * Regresa un nuevo objeto con propiedades por default para la fila
   * @returns
   */
  returnMeasurementRow(): IMeasurementRow {
    return {
      _id: '',
      status: this.measurementRowEnum.NEW,
      value: null,
      name: '',
      unitMeasurement: [],
      unitMeasurementDefault: null,
      unit: '',
      name_es: '',
    };
  }

  /**
   * Emite al componente padre cuando se guardan los datos
   * @returns
   */
  async onEmitSaveMeasurementsTable(): Promise<void> {
    this.formMeasurementTable.control.markAllAsTouched();
    this.formMeasurementTable.control.markAsDirty();
    if (!this.formMeasurementTable.valid) {
      this.messageAlertService.createMessage('error', this.messageAlertService.messages.incompleteData);
      return;
    }
    const data: PatientMeasurementRequestDTO = {
      dateMetadata: this.dateHelper.convertISOStringToDateMetadata(new Date()),
      measurements: this.utilitiesService.convertMeasurementsRowToMeasurements(this.measurementsRow),
      serviceId: this.serviceId ?? ''
    };
    this.save.emit(data);
    const measurementsValid = data.measurements.filter(measurement => measurement.measureId && measurement.measureUnitId && measurement.value);
    if (!this.forTemplates && measurementsValid.length > 0) {
      if (Array.isArray(this.measurementsRow)) {
        this.measurementsRow.splice(0, this.measurementsRow.length - 1);
      }
    }
  }

  /**
   * Cada que se cambia un valor
   * @param index índice del renglón
   */
  onChangeRow(index: number): void {
    this.changeRow.emit(index);
  }

  /**
   * Remueve la fila de la tabla
   * @param i
   * @param measurementId
   */
  onRemoveMeasurementTable(i, measurementId: string): void {
    this.removeRow.emit(measurementId);
    this.measurementsRow.splice(i, 1);
  }

  /**
   * Obtiene los primeros elementos de mediciones.
   */
  async getFirstMeasurements(): Promise<void> {
    try {
      const filter = {
        limit: this.perPage,
        skip: 0,
        where: {
          name_es: { like: '', options: 'i' },
          _id: {
            nin: this.measurementsRow.filter(item => item._id).map(item => item._id)
          },
          hidden: false,
        },
        order: 'name ASC',
      };
      this.isLoadingFirstMenus = true;
      await firstValueFrom((this.store.dispatch(new MeasurementAction.Get(filter))));
      this.isLoadingFirstMenus = false;
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
    }
  }

  /**
   * Se buscan mediciones.
   * @param value
   * @returns
   */
  onSearchMeasurement(value): void {
    if (value === this.searchTempValue) {
      return;
    }
    this.isLoadingFirstMenus = true;
    if (this.searchInterval) {
      clearInterval(this.searchInterval);
    }
    this.searchInterval = setTimeout(async () => {
      this.finishSearchMenus = false;
      this.searchTempValue = value;
      const filter = {
        limit: this.perPage,
        skip: 0,
        where: {
          name_es: { like: value, options: 'i' },
          _id: {
            nin: this.measurementsRow.filter(item => item._id).map(item => item._id)
          },
          hidden: false,
        },
        order: 'name ASC',
      };
      await firstValueFrom((this.store.dispatch(new MeasurementAction.Get(filter))));
      this.isLoadingFirstMenus = false;
    }, 400);
  }

  /**
   * Se cargan más elementos de mediciones.
   * @returns
   */
  async loadMoreMeasurements(): Promise<void> {
    if (this.finishSearchMenus) {
      return;
    }
    this.isLoadingMenus = true;
    this.totalBatches++;
    const filter = {
      limit: this.perPage,
      skip: this.totalBatches * this.perPage,
      where: {
        name: { like: this.searchTempValue, options: 'i' },
        _id: {
          nin: this.measurementsRow.filter(item => item._id).map(item => item._id)
        },
        hidden: false,
      },
      order: 'name ASC',
    };
    await firstValueFrom((this.store.dispatch(new MeasurementAction.Get(filter, true))));
    const measurements = cloneDeep(await firstValueFrom((this.store.selectOnce(MeasurementState.getMeasurementsType))));
    if (Array.isArray(measurements)) {
      this.finishSearchMenus = measurements.length % this.perPage !== 0 || measurements.length === 0;
    }
    this.isLoadingMenus = false;
  }

  async onCloseSelect($event, data: IMeasurementRow): Promise<void> {
    data = cloneDeep(data);
    if ($event) {
      data.showEdit = false;
      setTimeout(async () => {
        await this.getFirstMeasurements();
      }, 100);
    } else {
      this.isLoadingMenus = false;
      this.isLoadingFirstMenus = false;
      this.finishSearchMenus = false;
      this.searchTempValue = '';
      this.totalBatches = 0;
    }
  }
}
